import { CATEGORIES, todayStr } from "../../constants/categories";

const ROSE = "#E11D48";
const ROSE_LIGHT = "#FB7185";
const PINK = "#EC4899";

const pad = n => String(n).padStart(2, "0");

function getWeekKey(dateStr) {
  const d = new Date(dateStr);
  const start = new Date(d.getFullYear(), 0, 1);
  const wk = Math.ceil(((d - start) / 86400000 + start.getDay() + 1) / 7);
  return `${d.getFullYear()}-W${wk}`;
}

function computeGrowthScore(completedCount, streakNum, longestStreak, completionRate, categoriesUsed) {
  // Weighted score: consistency 40%, streak 25%, volume 20%, variety 15%
  const consistency = Math.min(completionRate / 100, 1) * 40;
  const streakScore = Math.min(streakNum / 30, 1) * 15 + Math.min(longestStreak / 30, 1) * 10;
  const volume = Math.min(completedCount / 50, 1) * 20;
  const variety = Math.min(categoriesUsed / 6, 1) * 15;
  return Math.round(consistency + streakScore + volume + variety);
}

function getScoreLabel(score) {
  if (score >= 80) return { text: "Xuất sắc", color: "#10B981" };
  if (score >= 60) return { text: "Rất tốt", color: "#14B8A6" };
  if (score >= 40) return { text: "Khá tốt", color: "#F59E0B" };
  if (score >= 20) return { text: "Đang tiến bộ", color: ROSE_LIGHT };
  return { text: "Mới bắt đầu", color: "var(--text-muted)" };
}

export default function InsightStyle({
  histMap, history, completedCount, streak, completionRate,
  calMonth, calYear, monthDays, firstDayOfWeek, monthNames,
  prevMonth, nextMonth, isCurrentMonth,
  selectedDay, setSelectedDay,
}) {
  const streakNum = streak.current_streak || 0;
  const longestStreak = streak.longest_streak || 0;
  const completed = history.filter(h => h.completed);

  // Category stats
  const catCounts = {};
  CATEGORIES.forEach(c => { catCounts[c.id] = 0; });
  completed.forEach(h => { if (catCounts[h.category_id] !== undefined) catCounts[h.category_id]++; });
  const categoriesUsed = Object.values(catCounts).filter(v => v > 0).length;
  const topCategory = CATEGORIES.reduce((best, c) =>
    (catCounts[c.id] || 0) > (catCounts[best.id] || 0) ? c : best, CATEGORIES[0]);

  // Growth Score
  const growthScore = computeGrowthScore(completedCount, streakNum, longestStreak, completionRate, categoriesUsed);
  const scoreLabel = getScoreLabel(growthScore);

  // Day-of-week pattern
  const dayOfWeekCounts = [0, 0, 0, 0, 0, 0, 0]; // Sun-Sat
  completed.forEach(h => {
    const d = new Date(h.date);
    dayOfWeekCounts[d.getDay()]++;
  });
  const maxDayCount = Math.max(1, ...dayOfWeekCounts);
  const dayLabels = ["CN", "T2", "T3", "T4", "T5", "T6", "T7"];
  const bestDayIdx = dayOfWeekCounts.indexOf(Math.max(...dayOfWeekCounts));

  // Weekly trend (last 10 weeks, always padded to 10)
  const weekMap = {};
  history.forEach(h => {
    const wk = getWeekKey(h.date);
    if (!weekMap[wk]) weekMap[wk] = { total: 0, completed: 0 };
    weekMap[wk].total++;
    if (h.completed) weekMap[wk].completed++;
  });
  const weeksToShow = 10;
  const now = new Date();
  const paddedTrend = [];
  for (let i = weeksToShow - 1; i >= 0; i--) {
    const d = new Date(now);
    d.setDate(d.getDate() - i * 7);
    const wk = getWeekKey(d.toISOString().split("T")[0]);
    const label = wk.split("-W")[1];
    const data = weekMap[wk] || { total: 0, completed: 0 };
    paddedTrend.push({
      key: wk,
      label,
      ...data,
      rate: data.total > 0 ? Math.round((data.completed / data.total) * 100) : 0,
    });
  }
  const weekTrend = paddedTrend;
  const maxWeekCompleted = Math.max(1, ...weekTrend.map(w => w.completed));

  // This month vs last month
  const thisMonthKey = `${calYear}-${pad(calMonth + 1)}`;
  const lastMonth = calMonth === 0 ? 11 : calMonth - 1;
  const lastMonthYear = calMonth === 0 ? calYear - 1 : calYear;
  const lastMonthKey = `${lastMonthYear}-${pad(lastMonth + 1)}`;

  const thisMonthEntries = history.filter(h => h.date.startsWith(thisMonthKey));
  const lastMonthEntries = history.filter(h => h.date.startsWith(lastMonthKey));
  const thisMonthCompleted = thisMonthEntries.filter(h => h.completed).length;
  const lastMonthCompleted = lastMonthEntries.filter(h => h.completed).length;
  const monthDiff = thisMonthCompleted - lastMonthCompleted;

  // Score ring SVG
  const ringSize = 120;
  const strokeWidth = 10;
  const radius = (ringSize - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const strokeDashoffset = circumference - (growthScore / 100) * circumference;

  return (
    <>
      {/* Growth Score */}
      <div style={{
        background: "var(--bg-card)",
        border: "1px solid var(--border-secondary)",
        borderRadius: "20px",
        padding: "24px 20px",
        marginBottom: "20px",
        textAlign: "center",
        position: "relative",
        overflow: "hidden",
        boxShadow: "var(--card-shadow)",
      }}>
        <div style={{ position: "absolute", inset: 0, background: `radial-gradient(circle at 50% 30%, ${ROSE}08, transparent 60%)`, pointerEvents: "none" }} />

        <div style={{ fontSize: "11px", color: ROSE, fontWeight: "600", letterSpacing: "2px", textTransform: "uppercase", marginBottom: "12px" }}>
          📈 Growth Score
        </div>

        <div style={{ position: "relative", display: "inline-block", marginBottom: "8px" }}>
          <svg width={ringSize} height={ringSize} style={{ transform: "rotate(-90deg)" }}>
            <circle cx={ringSize / 2} cy={ringSize / 2} r={radius}
              fill="none" stroke="var(--border-secondary)" strokeWidth={strokeWidth} />
            <circle cx={ringSize / 2} cy={ringSize / 2} r={radius}
              fill="none" stroke={`url(#scoreGradient)`} strokeWidth={strokeWidth}
              strokeDasharray={circumference} strokeDashoffset={strokeDashoffset}
              strokeLinecap="round"
              style={{ transition: "stroke-dashoffset 1s ease" }} />
            <defs>
              <linearGradient id="scoreGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                <stop offset="0%" stopColor={ROSE} />
                <stop offset="100%" stopColor={PINK} />
              </linearGradient>
            </defs>
          </svg>
          <div style={{
            position: "absolute", inset: 0, display: "flex", flexDirection: "column",
            alignItems: "center", justifyContent: "center",
          }}>
            <span style={{ fontSize: "32px", fontWeight: "900", color: scoreLabel.color }}>{growthScore}</span>
            <span style={{ fontSize: "10px", color: "var(--text-muted)" }}>/100</span>
          </div>
        </div>

        <div style={{ fontSize: "14px", fontWeight: "700", color: scoreLabel.color }}>{scoreLabel.text}</div>
        <div style={{ fontSize: "10px", color: "var(--text-muted)", marginTop: "4px" }}>
          Đều đặn {completionRate}% · Streak {streakNum} · {categoriesUsed}/6 lĩnh vực
        </div>
      </div>

      {/* Quick Stats Grid */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "10px", marginBottom: "20px" }}>
        {[
          { label: "Ngày mạnh nhất", value: dayLabels[bestDayIdx], sub: `${dayOfWeekCounts[bestDayIdx]} lần`, color: ROSE },
          { label: "Lĩnh vực yêu thích", value: topCategory.emoji, sub: topCategory.label, color: topCategory.color },
          { label: "So tháng trước", value: monthDiff > 0 ? `+${monthDiff}` : `${monthDiff}`, sub: monthDiff >= 0 ? "Tốt hơn" : "Cần cố gắng", color: monthDiff >= 0 ? "#10B981" : "#F59E0B" },
        ].map((stat, i) => (
          <div key={i} style={{
            background: "var(--bg-card)",
            border: "1px solid var(--border-secondary)",
            borderRadius: "14px",
            padding: "14px 10px",
            textAlign: "center",
            boxShadow: "var(--card-shadow)",
          }}>
            <div style={{ fontSize: "22px", fontWeight: "900", color: stat.color }}>{stat.value}</div>
            <div style={{ fontSize: "9px", color: "var(--text-muted)", fontWeight: "600", marginTop: "2px" }}>{stat.sub}</div>
            <div style={{ fontSize: "8px", color: "var(--text-muted)", marginTop: "4px" }}>{stat.label}</div>
          </div>
        ))}
      </div>

      {/* Weekly Trend */}
      <div style={{
        background: "var(--bg-card)",
        border: "1px solid var(--border-secondary)",
        borderRadius: "16px",
        padding: "20px",
        marginBottom: "20px",
      }}>
        <div style={{ fontSize: "13px", fontWeight: "700", color: "var(--text-primary)", marginBottom: "14px", display: "flex", alignItems: "center", gap: "6px" }}>
          <span>📊</span> Xu Hướng Tuần
        </div>
        {(() => {
          const maxBarHeight = 64;
          return (
            <div style={{ display: "flex", alignItems: "flex-end", gap: "4px", height: `${maxBarHeight + 30}px` }}>
              {weekTrend.map((w, i) => {
                const barH = w.completed > 0 ? Math.max((w.completed / maxWeekCompleted) * maxBarHeight, 12) : 3;
                const isLatest = i === weekTrend.length - 1;
                return (
                  <div key={w.key} style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", gap: "3px" }}>
                    <div style={{ fontSize: "8px", color: isLatest ? ROSE : "var(--text-muted)", fontWeight: "700" }}>
                      {w.completed > 0 ? w.completed : ""}
                    </div>
                    <div style={{
                      width: "100%",
                      height: `${barH}px`,
                      borderRadius: "3px 3px 1px 1px",
                      background: w.completed > 0
                        ? isLatest ? `linear-gradient(180deg, ${ROSE}, ${PINK})` : `linear-gradient(180deg, ${ROSE}88, ${ROSE}44)`
                        : "var(--border-secondary)",
                      transition: "height 0.5s ease",
                      boxShadow: isLatest && w.completed > 0 ? `0 0 8px ${ROSE}33` : "none",
                    }} />
                    <div style={{ fontSize: "8px", color: isLatest ? ROSE : "var(--text-muted)" }}>W{w.label}</div>
                  </div>
                );
              })}
            </div>
          );
        })()}
      </div>

      {/* Day of Week Pattern */}
      <div style={{
        background: "var(--bg-card)",
        border: "1px solid var(--border-secondary)",
        borderRadius: "16px",
        padding: "20px",
        marginBottom: "20px",
      }}>
        <div style={{ fontSize: "13px", fontWeight: "700", color: "var(--text-primary)", marginBottom: "14px", display: "flex", alignItems: "center", gap: "6px" }}>
          <span>🗓️</span> Thói Quen Theo Ngày
        </div>
        <div style={{ display: "flex", gap: "6px" }}>
          {[1, 2, 3, 4, 5, 6, 0].map(dayIdx => {
            const count = dayOfWeekCounts[dayIdx];
            const intensity = maxDayCount > 0 ? count / maxDayCount : 0;
            const isBest = dayIdx === bestDayIdx && count > 0;
            return (
              <div key={dayIdx} style={{ flex: 1, textAlign: "center" }}>
                <div style={{ fontSize: "11px", color: isBest ? ROSE : "var(--text-muted)", fontWeight: isBest ? "800" : "500", marginBottom: "6px" }}>
                  {dayLabels[dayIdx]}
                </div>
                <div style={{
                  aspectRatio: "1",
                  borderRadius: "10px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  background: count > 0 ? `${ROSE}${pad(Math.round(intensity * 200 + 20).toString(16))}` : "var(--bg-inset)",
                  border: isBest ? `2px solid ${ROSE}` : "2px solid transparent",
                  boxSizing: "border-box",
                  transition: "all 0.3s",
                }}>
                  <span style={{
                    fontSize: "13px",
                    fontWeight: "700",
                    color: intensity > 0.5 ? "#fff" : count > 0 ? ROSE_LIGHT : "var(--text-dim)",
                  }}>{count}</span>
                </div>
                {isBest && (
                  <div style={{ fontSize: "8px", color: ROSE, fontWeight: "700", marginTop: "4px" }}>TOP</div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Category Breakdown */}
      <div style={{
        background: "var(--bg-card)",
        border: "1px solid var(--border-secondary)",
        borderRadius: "16px",
        padding: "20px",
        marginBottom: "20px",
      }}>
        <div style={{ fontSize: "13px", fontWeight: "700", color: "var(--text-primary)", marginBottom: "14px", display: "flex", alignItems: "center", gap: "6px" }}>
          <span>🎯</span> Phân Bổ Lĩnh Vực
        </div>

        {/* Horizontal stacked bar */}
        {completedCount > 0 && (
          <div style={{ display: "flex", borderRadius: "8px", overflow: "hidden", height: "12px", marginBottom: "14px" }}>
            {CATEGORIES.map(cat => {
              const count = catCounts[cat.id] || 0;
              if (count === 0) return null;
              return (
                <div key={cat.id} style={{
                  width: `${(count / completedCount) * 100}%`,
                  background: cat.color,
                  minWidth: "4px",
                  transition: "width 0.5s",
                }} />
              );
            })}
          </div>
        )}

        {/* Category list */}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "8px" }}>
          {CATEGORIES.map(cat => {
            const count = catCounts[cat.id] || 0;
            const percent = completedCount > 0 ? Math.round((count / completedCount) * 100) : 0;
            return (
              <div key={cat.id} style={{
                display: "flex", alignItems: "center", gap: "8px",
                padding: "8px 10px",
                borderRadius: "10px",
                background: count > 0 ? `${cat.color}10` : "var(--bg-inset)",
              }}>
                <span style={{ fontSize: "16px" }}>{cat.emoji}</span>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: "11px", fontWeight: "600", color: count > 0 ? "var(--text-secondary)" : "var(--text-dim)" }}>
                    {cat.label}
                  </div>
                  <div style={{ fontSize: "10px", color: count > 0 ? cat.color : "var(--text-dim)" }}>
                    {count} lần · {percent}%
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Monthly Calendar */}
      <div style={{
        background: "var(--bg-card)",
        border: "1px solid var(--border-secondary)",
        borderRadius: "16px",
        padding: "20px",
        marginBottom: "20px",
      }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "16px" }}>
          <button onClick={prevMonth} style={{ background: "none", border: "none", color: "var(--text-tertiary)", cursor: "pointer", fontSize: "20px", padding: "4px 10px" }}>‹</button>
          <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
            <div style={{ fontSize: "16px", fontWeight: "700", color: "var(--text-primary)" }}>{monthNames[calMonth]} {calYear}</div>
            <span style={{
              fontSize: "10px", fontWeight: "700", color: ROSE,
              background: `${ROSE}15`, padding: "2px 8px", borderRadius: "8px",
            }}>
              {thisMonthCompleted}/{thisMonthEntries.length}
            </span>
          </div>
          <button onClick={nextMonth} style={{ background: "none", border: "none", color: isCurrentMonth ? "var(--border-primary)" : "var(--text-tertiary)", cursor: isCurrentMonth ? "default" : "pointer", fontSize: "20px", padding: "4px 10px" }}>›</button>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: "6px", textAlign: "center" }}>
          {["T2", "T3", "T4", "T5", "T6", "T7", "CN"].map(d => (
            <div key={d} style={{ fontSize: "11px", color: "var(--text-dim)", padding: "6px 0", fontWeight: "700" }}>{d}</div>
          ))}
          {Array.from({ length: (firstDayOfWeek + 6) % 7 }).map((_, i) => <div key={`e${i}`} />)}
          {Array.from({ length: monthDays }).map((_, i) => {
            const day = i + 1;
            const dateStr = `${calYear}-${pad(calMonth + 1)}-${pad(day)}`;
            const items = histMap[dateStr] || [];
            const hasCompleted = items.some(c => c.completed);
            const hasData = items.length > 0;
            const isToday = dateStr === todayStr;
            const isFuture = dateStr > todayStr;
            const isSelected = Array.isArray(selectedDay) && selectedDay[0]?.date === dateStr;
            const cat = hasCompleted ? CATEGORIES.find(c => c.id === items.find(x => x.completed)?.category_id) : null;

            return (
              <div key={day} onClick={() => !isFuture && hasData && setSelectedDay(items)}
                style={{
                  aspectRatio: "1",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  borderRadius: "10px",
                  cursor: hasData && !isFuture ? "pointer" : "default",
                  background: isSelected ? `${ROSE}25` : hasCompleted ? `${cat?.color || ROSE}15` : "transparent",
                  border: isToday ? `2px solid ${ROSE}` : isSelected ? `2px solid ${ROSE}88` : "1.5px solid transparent",
                  boxSizing: "border-box",
                  transition: "all 0.2s",
                  position: "relative",
                }}>
                <span style={{
                  fontSize: "12px",
                  fontWeight: isToday || hasCompleted ? "700" : "500",
                  color: hasCompleted ? (cat?.color || ROSE) : isFuture ? "var(--border-primary)" : isToday ? ROSE : hasData ? "var(--text-tertiary)" : "var(--text-dim)",
                }}>{day}</span>
                {hasCompleted && (
                  <div style={{
                    position: "absolute", bottom: "2px",
                    width: "10px", height: "3px", borderRadius: "2px",
                    background: cat?.color || ROSE,
                  }} />
                )}
                {isToday && !hasCompleted && (
                  <div style={{
                    position: "absolute", bottom: "3px",
                    width: "4px", height: "4px", borderRadius: "50%",
                    background: ROSE, animation: "pulse 2s infinite",
                  }} />
                )}
              </div>
            );
          })}
        </div>
      </div>
    </>
  );
}
